import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { oktaAuth } from '../utils/okta';
import { API } from './api';
import type { IAnalysts, IRow, IRowDetails } from '../types/TableRows';
import { toast } from 'react-toastify';

interface ErrorResponse {
  status: number;
  data: {
    message: string;
  };
}

export const reconApiSlice = createApi({
  reducerPath: 'api',
  baseQuery: fetchBaseQuery({
    baseUrl: API.BaseURL,
    credentials: 'include',
    prepareHeaders: (headers) => {
      const tokens = oktaAuth.tokenManager.getTokensSync();

      if (!tokens.accessToken) return headers;

      headers.set('Content-Type', API.contentType);
      headers.set('Authorization', `${tokens.accessToken.tokenType} ${tokens.accessToken.accessToken}`);

      return headers;
    },
  }),
  tagTypes: ['ReconGridData', 'ReconGridDataDetails'],
  endpoints: (build) => ({
    getReconData: build.query<IRow[], { type: string; reviewDate: string | null; email: string }>({
      transformResponse: (baseQueryReturnValue?: IRow[]): IRow[] => baseQueryReturnValue!,
      transformErrorResponse: (response: ErrorResponse) => {
        toast.error(`Error loading grid data: ${response.data.message}`);
        return response;
      },
      query: ({ type = 'UNMATCHED', reviewDate = '', email = 'all' }) =>
        `${API.endpoints.ReconGridData}?type=${type}&reviewDate=${reviewDate!}&email=${email}`,
      providesTags: (result) =>
        result
          ? [...result.map(({ breakId }) => ({ type: 'ReconGridData' as const, breakId })), 'ReconGridData']
          : ['ReconGridData'],
    }),
    getReconDataDetails: build.query<IRowDetails, { breakId: string }>({
      transformResponse: (baseQueryReturnValue?: IRowDetails): IRowDetails => baseQueryReturnValue!,
      transformErrorResponse: (response: ErrorResponse) => {
        toast.error(`Error loading grid data: ${response.data.message}`);
        return response;
      },
      query: ({ breakId = '' }) => `${API.endpoints.ReconGridDataDetails}?breakId=${breakId}`,
    }),
    getReconAnalysts: build.query<IAnalysts[], string>({
      transformResponse: (baseQueryReturnValue?: IAnalysts[]): IAnalysts[] => baseQueryReturnValue!,
      transformErrorResponse: (response: ErrorResponse) => {
        toast.error(`Error loading grid data: ${response.data.message}`);
        return response;
      },
      query: () => API.endpoints.ReconGridAnalysts,
    }),
  }),
});

export const { useGetReconDataQuery, useGetReconDataDetailsQuery, useGetReconAnalystsQuery } = reconApiSlice;
